import { Alert, Button, Modal, Popconfirm, Space, Steps, Tag, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { memo, useCallback, useState } from 'react'

import {
  addTeachersToSalaryPayoutBatch,
  cancelSalaryPayoutBatch,
  completeSalaryPayoutBatch,
} from '~/services/salary-payout'
import {
  ISalaryPayoutBatchAddResult,
  ISalaryPayoutBatchMonthRes,
  ISalaryPayoutBatchRes,
} from '~/types/salary-payout'

import { BatchCard } from './style'

interface Props {
  year: number
  month: number
  data: ISalaryPayoutBatchMonthRes | null
  loading: boolean
  /** 表格里勾选、且可以加入批次的老师 */
  selectedTeacherIds: (string | number)[]
  /** 批次或某位老师的状态变了：刷新批次和表格 */
  onChanged: () => void
}

/** 每次请求加入的老师数 */
const ADD_CHUNK = 5

const errorText = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message
  || (err as Error)?.message
  || fallback

/** 本月正在用的批次：最新一个没取消的 */
export const activeBatchOf = (data: ISalaryPayoutBatchMonthRes | null): ISalaryPayoutBatchRes | null =>
  data?.batches.find((b) => b.status !== 'CANCELLED') ?? null

const fmtAmount = (v: string | number | null | undefined) => {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v)
}

/**
 * 月度批量转账：所有老师的转账建在同一个 Wise 批次里，管理员在 Wise 里只付一次款。
 * 日本企业号用 Personal API token 不能通过 API 付款，所以第 ④ 步只能去 wise.com 完成。
 */
const BatchPanel: React.FC<Props> = ({ year, month, data, loading, selectedTeacherIds, onChanged }) => {
  const [action, setAction] = useState<'add' | 'complete' | 'cancel' | null>(null)
  const [results, setResults] = useState<ISalaryPayoutBatchAddResult[] | null>(null)

  const batch = activeBatchOf(data)
  const cancelledCount = data?.batches.filter((b) => b.status === 'CANCELLED').length ?? 0
  const ym = `${year}-${String(month).padStart(2, '0')}`
  const currency = batch?.sourceCurrency ?? data?.sourceCurrency ?? ''

  const total = Number(batch?.totalSourceAmount ?? 0)
  const balance = data?.availableBalance == null ? null : Number(data.availableBalance)
  const short = balance != null && batch != null && batch.transferCount > 0 && !batch.funded && balance < total

  const canAdd = (!batch || batch.status === 'OPEN') && selectedTeacherIds.length > 0
  const canComplete = batch?.status === 'OPEN' && batch.transferCount > 0
  const canCancel = !!batch && !batch.funded

  const current = !batch ? 0 : batch.funded ? 4 : batch.status === 'COMPLETED' ? 3 : batch.transferCount > 0 ? 2 : 1

  const handleAdd = useCallback(async () => {
    setAction('add')
    const ids = selectedTeacherIds.map((id) => Number(id))
    const collected: ISalaryPayoutBatchAddResult[] = []
    try {
      // 每位老师要报价 + 建转账，分几次提交，单个请求不会拖过网关超时（超时后的重试会被后端的批次锁挡住）
      for (let i = 0; i < ids.length; i += ADD_CHUNK) {
        const res = await addTeachersToSalaryPayoutBatch(year, month, ids.slice(i, i + ADD_CHUNK))
        collected.push(...res.data.results)
      }
      if (collected.every((r) => r.added)) message.success(`已加入批次：${collected.length} 位老师`)
    } catch (err) {
      message.error(errorText(err, '加入批次失败'))
    } finally {
      if (collected.length) setResults(collected)
      setAction(null)
      onChanged()
    }
  }, [year, month, selectedTeacherIds, onChanged])

  const handleComplete = useCallback(async () => {
    if (!batch) return
    setAction('complete')
    try {
      await completeSalaryPayoutBatch(batch.id)
      message.success('批次已锁定，可以去 Wise 付款了')
      onChanged()
    } catch (err) {
      message.error(errorText(err, '锁定失败'))
    } finally {
      setAction(null)
    }
  }, [batch, onChanged])

  const handleCancel = useCallback(async () => {
    if (!batch) return
    setAction('cancel')
    try {
      await cancelSalaryPayoutBatch(batch.id)
      message.success('批次已取消，可以重新勾选老师生成新批次')
      onChanged()
    } catch (err) {
      message.error(errorText(err, '取消失败'))
    } finally {
      setAction(null)
    }
  }, [batch, onChanged])

  const failed = results?.filter((r) => !r.added) ?? []

  return (
    <BatchCard>
      <div className="head">
        <Space size={8} wrap>
          <strong>{ym} 批量转账</strong>
          {!batch && <Tag>未生成</Tag>}
          {batch?.status === 'OPEN' && <Tag color="blue">待锁定</Tag>}
          {batch?.status === 'COMPLETED' && !batch.funded && <Tag color="gold">已锁定 · 待在 Wise 付款</Tag>}
          {batch?.funded && <Tag color="green">已付款</Tag>}
          {!!batch?.returned?.length && <Tag color="red">{batch.returned.length} 笔被银行退回</Tag>}
          {cancelledCount > 0 && <span className="muted">本月已取消 {cancelledCount} 个批次</span>}
        </Space>
        <Space size={8} wrap>
          <Popconfirm
            title={`把选中的 ${selectedTeacherIds.length} 位老师加入批次？`}
            description={
              <div style={{ maxWidth: 320 }}>
                {batch ? '' : `会先在 Wise 建一个「Teacher salary ${ym}」批次。`}
                每位老师会按本月最新的薪资重新报价，再在批次里建转账，还不会扣钱。人多时要等一会儿。
              </div>
            }
            okText="加入"
            cancelText="取消"
            onConfirm={handleAdd}
            disabled={!canAdd || !!action}
          >
            <Button type="primary" loading={action === 'add'} disabled={!canAdd || !!action}>
              {batch ? '加入批次' : '生成批次'}（{selectedTeacherIds.length}）
            </Button>
          </Popconfirm>
          <Popconfirm
            title="锁定批次？"
            description={
              <div style={{ maxWidth: 320 }}>
                锁定后不能再加人，需要补发的老师只能单笔发放。锁定后去 Wise 付款一次就发给批次里的全部老师。
              </div>
            }
            okText="锁定"
            cancelText="取消"
            onConfirm={handleComplete}
            disabled={!canComplete || !!action}
          >
            <Button loading={action === 'complete'} disabled={!canComplete || !!action}>
              锁定批次
            </Button>
          </Popconfirm>
          <Popconfirm
            title="取消整个批次？"
            description={
              <div style={{ maxWidth: 320 }}>
                Wise 不能从批次里单独拿掉一笔，取消会作废批次里所有老师的转账，之后可以重新勾选生成新批次。
              </div>
            }
            okText="取消批次"
            okButtonProps={{ danger: true }}
            cancelText="不取消"
            onConfirm={handleCancel}
            disabled={!canCancel || !!action}
          >
            <Button danger loading={action === 'cancel'} disabled={!canCancel || !!action}>
              取消批次
            </Button>
          </Popconfirm>
          <Button href="https://wise.com/" target="_blank" rel="noopener noreferrer">
            打开 Wise ↗
          </Button>
        </Space>
      </div>

      <Steps
        size="small"
        current={current}
        className="steps"
        items={[
          { title: '勾选老师', description: '表格里勾选要发的老师' },
          { title: '生成批次', description: '报价 + 在批次里建转账' },
          { title: '核对明细', description: '逐个看下面表格的实付和到账' },
          { title: '锁定批次', description: '之后不能再加人' },
          { title: '去 Wise 付一次款', description: '付完状态会自动同步' },
        ]}
      />

      <div className="stats">
        <div>
          <span className="label">笔数</span>
          <span className="value">{batch ? batch.transferCount : '—'}</span>
        </div>
        <div>
          <span className="label">付款合计</span>
          <span className="value">
            {batch ? `${fmtAmount(batch.totalSourceAmount)} ${currency}` : '—'}
          </span>
        </div>
        <div>
          <span className="label">其中手续费</span>
          <span className="value">{batch ? `${fmtAmount(batch.totalFee)} ${currency}` : '—'}</span>
        </div>
        <div>
          <span className="label">Wise 余额</span>
          <span className={`value ${short ? 'danger' : ''}`}>
            {loading ? '…' : balance == null ? '读不到' : `${fmtAmount(balance)} ${currency}`}
          </span>
        </div>
        <div>
          <span className="label">Wise 批次</span>
          <span className="value mono">
            {batch ? (
              <Typography.Text copyable={{ text: batch.name }} style={{ fontSize: 13 }}>
                {batch.name}
              </Typography.Text>
            ) : (
              '—'
            )}
          </span>
        </div>
      </div>

      {short && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={`Wise 余额不够：要付 ${fmtAmount(total)} ${currency}，余额只有 ${fmtAmount(balance)} ${currency}`}
          description="先往 Wise 的余额里充值，再去付款；余额不够时 Wise 会拒绝这次付款。"
        />
      )}

      {batch?.status === 'COMPLETED' && !batch.funded && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="最后一步：去 Wise 付款"
          description={
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>登录 wise.com，切到企业账户（Shaokai Services）。</li>
              <li>
                找到批次「<b>{batch.name}</b>」（共 {batch.transferCount} 笔，合计 {fmtAmount(batch.totalSourceAmount)}{' '}
                {currency}），选用余额付款，输入密码并在 Wise App 里批准。
              </li>
              <li>付完不用回来点任何按钮，每位老师的状态会通过 Wise 的通知自动变成「Wise处理中 / 钱已寄出」。</li>
            </ol>
          }
        />
      )}

      {!!batch?.returned?.length && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={`${batch.returned.length} 笔转账付款后被老师的银行退回，钱已退回 Wise 余额`}
          description={
            <div>
              <ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
                {batch.returned.map((r) => (
                  <li key={String(r.teacherId)}>
                    <b>{r.teacherName}</b>：{fmtAmount(r.payout.wiseTargetAmount ?? '')} {r.payout.targetCurrency ?? ''}
                    {r.payout.status === 'REFUNDED' ? '（已退款）' : '（银行退回）'}
                  </li>
                ))}
              </ul>
              <div>
                最常见的原因是户名和银行登记的不一致（日本账户要用存折上的片假名）。请老师在「收款信息」里改正后重新审核，
                再在下方表格里点这位老师，单独重新报价发放。退回的金额和手续费以 Wise 后台的退款记录为准。
              </div>
            </div>
          }
        />
      )}

      {batch?.status === 'OPEN' && batch.transferCount > 0 && (
        <div className="hint">
          锁定前可以继续勾选老师加入。要从批次里拿掉某位老师，只能取消整个批次再重新生成。
          批次创建于 {dayjs(batch.createdAt).format('MM-DD HH:mm')}。
        </div>
      )}

      <Modal
        open={!!results}
        title={failed.length ? `${failed.length} 位老师没有加入批次` : '已全部加入批次'}
        onCancel={() => setResults(null)}
        onOk={() => setResults(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText="知道了"
      >
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {(results ?? []).map((r) => (
            <li key={String(r.teacherId)} style={{ marginBottom: 6 }}>
              <b>{r.teacherName}</b>{' '}
              {r.added ? (
                <Tag color="green">{r.message ?? '已加入'}</Tag>
              ) : (
                <>
                  <Tag color="red">未加入</Tag>
                  <span style={{ color: '#6b7280' }}>{r.message}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </Modal>
    </BatchCard>
  )
}

export default memo(BatchPanel)
